import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/redux';
import { FormInputText } from '../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { ButtonClassNames, ModalVariants, InputTextFormat, InputRegexPattern } from '../../../../models/Common';
import { ITerminationStep, ITerminateForm } from '../../../../models/ContractTermination';
import { formatDateAsISO, getErrorMessage } from '../../../../utils';

interface TerminateDetailsProps {
  formState: ITerminateForm;
  changeTerminateStep: (step: ITerminationStep) => void;
}

interface TerminateDetailsFormState {
  name: string;
  itn: string;
  iban: string;
}

export const TerminateDetails: FC<TerminateDetailsProps> = ({ formState, changeTerminateStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [createUserDocument, { isLoading }] = userInfoApi.useCreateUserDocumentMutation();
  const methods = useForm<TerminateDetailsFormState>({
    defaultValues: { name: '', itn: '', iban: '' },
  });
  const { handleSubmit } = methods;

  const handleProceed = async (data: TerminateDetailsFormState) => {
    try {
      const toDate = formatDateAsISO(
        Number(formState.terminationDate?.day),
        Number(formState.terminationDate?.month),
        Number(formState.terminationDate?.year),
      );
      const terminationPhone = `+38${formState.phone}`;
      const body = {
        toDate,
        terminationPhone,
        refundDetails: {
          fullName: data.name.trim(),
          taxId: data.itn,
          iban: `UA${data.iban}`,
        },
      };
      await createUserDocument({ docType: 'cashless', body }).unwrap();
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('terminateContract.applicationSent')}`,
            description: `${t('terminateContract.supportManagerWillContactYou')}`,
          },
        }),
      );
      changeTerminateStep(undefined);
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  return (
    <FormProvider {...methods}>
      <article className="account__terminate__title">
        <span>{t('terminateContract.fillAccountDetails')}</span>
      </article>
      <form className="account__terminate__details" onSubmit={handleSubmit(handleProceed)}>
        <div className="account__terminate__details__inputs">
          <div className="account__terminate__details__input">
            <FormInputText name="name" label={t('terminateContract.recipientName')} className="primary" isRequired />
          </div>
          <div className="account__terminate__details__input">
            <FormInputText
              name="itn"
              label={t('terminateContract.taxNumber')}
              className="primary"
              isRequired
              minLength={9}
              format={InputTextFormat.ContractNumberOrPhone}
              pattern={InputRegexPattern.OnlyNumbers}
            />
          </div>
          <div className="account__terminate__details__input">
            <FormInputText
              name="iban"
              label={t('terminateContract.internationalNumber')}
              className="primary"
              isRequired
              minLength={27}
              format={InputTextFormat.Iban}
              pattern={InputRegexPattern.OnlyNumbers}
            />
          </div>
        </div>
        <div className="modals__btns-wrapper">
          <div className="btn-group">
            <CustomMUIButton
              label={t('common.back')}
              type="button"
              className={ButtonClassNames.PrimaryFulfilled}
              handleClick={() => changeTerminateStep('refund')}
              isBold
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('terminateContract.sendApplication')}
              type="submit"
              className={ButtonClassNames.SecondaryOutlined}
              isDisabled={isLoading}
              isBold
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
